/**
 * Reusable horizontal product strip used for context-aware surfacing:
 *  • Workout completion — module-specific picks
 *  • Trainer profile — recommended products (pass `products` + `tag="Trainer recommends"`)
 *  • Home smart-rec — single-card variant via `single`
 *
 * Renders up to 3 products + a trailing "Shop →" link that deep-links to /marketplace.
 */
import { useNavigate } from "react-router-dom";
import { ShoppingBag, ChevronRight } from "lucide-react";

export function EmbeddedProductCard({ product, onClick, tag, compact = false }) {
  if (!product) return null;
  const photo = product.photo || (product.photos || [])[0];
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`shop-card-${product.product_id}`}
      className="snap-start shrink-0 text-left bg-white rounded-[14px] overflow-hidden border border-line2"
      style={{
        width: compact ? "100%" : 160,
        boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
      }}
    >
      <div className="relative bg-canvas2" style={{ aspectRatio: "1/1" }}>
        {photo ? (
          <img src={photo} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full grid place-items-center text-ink-muted text-xs">
            No image
          </div>
        )}
        {tag && (
          <span
            className="absolute top-2 left-2 px-2 h-6 inline-flex items-center text-[10px] font-bold uppercase rounded-pill"
            style={{
              background: "rgba(0,212,255,0.95)",
              color: "#000",
              borderRadius: 100,
              letterSpacing: "0.04em",
            }}
          >
            {tag}
          </span>
        )}
      </div>
      <div className="p-2.5">
        <div
          className="text-[13px] font-semibold leading-tight line-clamp-2"
          style={{ color: "#1A1A1A", minHeight: 32 }}
        >
          {product.title}
        </div>
        <div className="mt-1.5 font-barlow-cond font-black text-[17px]" style={{ color: "#1A1A1A" }}>
          ₹{(product.price_inr || 0).toLocaleString("en-IN")}
        </div>
      </div>
    </button>
  );
}

export default function MarketplaceStrip({
  headline,
  sub,
  products = [],
  shopHref = "/marketplace",
  testid = "marketplace-strip",
  tag,
  trainerRef,
}) {
  const navigate = useNavigate();
  if (!products || products.length === 0) return null;

  const openProduct = (p) => {
    // Persist trainer referral (if any) so "Add to Cart" credits the pro
    if (trainerRef) {
      try {
        sessionStorage.setItem("ff_trainer_ref", trainerRef);
      } catch {
        /* ignore */
      }
    }
    const qs = trainerRef ? `?trainer_ref=${encodeURIComponent(trainerRef)}` : "";
    navigate(`/marketplace/product/${p.product_id}${qs}`);
  };

  return (
    <section className="mt-4" data-testid={testid}>
      <div className="flex items-end justify-between mb-2.5">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <ShoppingBag size={14} style={{ color: "#00B0D5" }} />
            <span
              className="text-[10px] uppercase tracking-[0.14em] font-bold"
              style={{ color: "#00B0D5" }}
            >
              From the Shop
            </span>
          </div>
          <h3 className="font-barlow-cond font-extrabold text-[20px] mt-0.5 leading-tight">
            {headline}
          </h3>
          {sub && <p className="text-[12px] text-ink-soft">{sub}</p>}
        </div>
        <button
          type="button"
          onClick={() => navigate(shopHref)}
          data-testid={`${testid}-shop-link`}
          className="shrink-0 inline-flex items-center gap-0.5 text-[13px] font-bold"
          style={{ color: "#00B0D5" }}
        >
          Shop <ChevronRight size={14} />
        </button>
      </div>
      <div
        className="flex gap-3 overflow-x-auto snap-x -mx-4 px-4 pb-1"
        style={{ scrollbarWidth: "none" }}
      >
        {products.slice(0, 3).map((p) => (
          <EmbeddedProductCard
            key={p.product_id}
            product={p}
            onClick={() => openProduct(p)}
            tag={tag}
          />
        ))}
      </div>
    </section>
  );
}
